import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { priceBookItemsApi } from '../api';
import { dollarsToCents, centsToDollarInput } from '../constants';
import { MoneyInput } from './MoneyInput';
import { UNIT_TYPE_OPTIONS, COST_CLASS_OPTIONS, PRICING_STATUS_STYLES, pricingStatusLabel } from '../priceBookConstants';
import { CategorySelect } from './CategorySelect';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const Label = ({ children }) => <label className="text-sm text-gray-300 mb-1 block font-medium">{children}</label>;
const Help = ({ children }) => <p className="text-[11px] text-gray-600 mt-1">{children}</p>;

const moneyOrNull = (raw) => {
  const s = String(raw ?? '').trim();
  return s === '' ? null : dollarsToCents(s);
};

export const ItemFormDialog = ({ open, onOpenChange, item, onSaved }) => {
  const isNew = !item;
  const archived = item?.is_archived;
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [f, setF] = useState({});

  useEffect(() => {
    if (!open) return;
    setF({
      item_code: item?.item_code || '', name: item?.name || '', description: item?.description || '',
      category: item?.category || '', cost_classification: item?.cost_classification || '',
      unit_type: item?.unit_type || 'each', custom_unit_label: item?.custom_unit_label || '',
      internal_unit_cost_cents: item?.internal_unit_cost_cents == null ? '' : centsToDollarInput(item.internal_unit_cost_cents),
      investor_unit_price_cents: item?.investor_unit_price_cents == null ? '' : centsToDollarInput(item.investor_unit_price_cents),
      standard_unit_price_cents: item?.standard_unit_price_cents == null ? '' : centsToDollarInput(item.standard_unit_price_cents),
      default_quantity_decimal: item?.default_quantity_decimal || '',
      internal_notes: item?.internal_notes || '',
    });
  }, [open, item]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const titleCase = (v) => v.replace(/(^|[\s\-/])([a-z])/g, (m, p, c) => p + c.toUpperCase());

  const save = async () => {
    if (busy) return;
    if (!String(f.name || '').trim()) { toast.error('Item name is required'); return; }
    const body = {
      item_code: f.item_code || null, name: f.name, description: f.description || null,
      category: f.category || null, cost_classification: f.cost_classification || null,
      unit_type: f.unit_type, custom_unit_label: f.unit_type === 'other' ? (f.custom_unit_label || null) : null,
      internal_unit_cost_cents: moneyOrNull(f.internal_unit_cost_cents),
      investor_unit_price_cents: moneyOrNull(f.investor_unit_price_cents),
      standard_unit_price_cents: moneyOrNull(f.standard_unit_price_cents),
      default_quantity_decimal: String(f.default_quantity_decimal || '').trim() || null,
      internal_notes: f.internal_notes || null,
    };
    for (const k of ['internal_unit_cost_cents', 'investor_unit_price_cents', 'standard_unit_price_cents']) {
      if (body[k] !== null && (body[k] === undefined || body[k] < 0)) { toast.error('Prices must be $0.00 or more'); return; }
    }
    setBusy(true);
    try {
      const res = isNew ? await priceBookItemsApi.create(body) : await priceBookItemsApi.update(item.id, body);
      toast.success(isNew ? 'Item created' : 'Item saved');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save item');
    } finally { setBusy(false); }
  };

  const action = async (fn, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNew ? 'New Price Book Item' : archived ? 'Archived Item' : 'Edit Item'}
            {item && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${PRICING_STATUS_STYLES[item.pricing_status]}`}>{pricingStatusLabel(item.pricing_status)}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Reusable estimating line item. Leave prices blank if not established yet — blank is not $0.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Item Code</Label>
              <Input value={f.item_code ?? ''} onChange={(e) => set('item_code', e.target.value)} placeholder={isNew ? 'Auto' : 'FLR-001'} disabled={archived} className={inputCls} data-testid="item-code" />
              {isNew && <Help>Leave blank to auto-number from the category code (e.g., FLR-001).</Help>}
            </div>
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={f.name ?? ''} onChange={(e) => set('name', titleCase(e.target.value))} placeholder="Install LVP — Labor Only" disabled={archived} className={inputCls} data-testid="item-name" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={f.description ?? ''} onChange={(e) => set('description', e.target.value)} disabled={archived} className={inputCls} data-testid="item-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <CategorySelect value={f.category ?? ''} onChange={(v) => set('category', v)} disabled={archived} data-testid="item-category" />
            </div>
            <div>
              <Label>Cost Classification</Label>
              <select value={f.cost_classification ?? ''} onChange={(e) => set('cost_classification', e.target.value)} disabled={archived} className={selectCls} data-testid="item-classification">
                <option value="">Not classified</option>
                {COST_CLASS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Unit Type *</Label>
              <select value={f.unit_type ?? 'each'} onChange={(e) => set('unit_type', e.target.value)} disabled={archived} className={selectCls} data-testid="item-unit">
                {UNIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {f.unit_type === 'other' && (
              <div>
                <Label>Custom Unit</Label>
                <Input value={f.custom_unit_label ?? ''} onChange={(e) => set('custom_unit_label', e.target.value)} placeholder="Load" disabled={archived} className={inputCls} data-testid="item-custom-unit" />
              </div>
            )}
            <div>
              <Label>Default Quantity</Label>
              <Input type="number" min="0" step="any" value={f.default_quantity_decimal ?? ''} onChange={(e) => set('default_quantity_decimal', e.target.value)} placeholder="1" disabled={archived} className={inputCls} data-testid="item-default-qty" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Price ($)</Label>
              <MoneyInput placeholder="Not set" value={f.standard_unit_price_cents ?? ''} onChange={(v) => set('standard_unit_price_cents', v)} disabled={archived} className={inputCls} data-testid="item-standard-price" />
              <Help>Your normal selling price for one unit.</Help>
            </div>
          </div>
          <div>
            <Label>Internal Notes (internal only)</Label>
            <Input value={f.internal_notes ?? ''} onChange={(e) => set('internal_notes', e.target.value)} disabled={archived} className={inputCls} data-testid="item-notes" />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
          {archived ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => priceBookItemsApi.restore(item.id), 'Item restored')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="item-restore">Restore Item</Button>
            </>
          ) : (
            <>
              {!isNew && (
                <Button variant="outline" onClick={() => setConfirmDelete(true)} disabled={busy} className="border-red-900 text-red-400 mr-auto" data-testid="item-delete">Delete</Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="item-save">
                {busy && <Loader2 size={14} className="animate-spin" />} {isNew ? 'Create Item' : 'Save'}
              </Button>
            </>
          )}
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="item-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{item?.name}"?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                The item is permanently removed from the Price Book and from any assemblies that use it.
                Quotes already built with it keep their own copy and are not affected. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setConfirmDelete(false); action(() => priceBookItemsApi.delete(item.id), 'Item deleted'); }} className="bg-red-600 text-white hover:bg-red-700" data-testid="item-delete-confirm-btn">
                Delete Item
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
